import { useState } from "react";
import { toast } from "react-toastify";
import { useMutation } from "react-query";
import { useForm } from "react-hook-form";
import { ScaleLoader } from "react-spinners";
import { updateCognitoUserPassword } from "api/user";
import DashboardHeader from "components/dashboard-header";
import InputFieldLabel from "components/input-field-label";
import { ReactComponent as EyeOpen } from "assets/svgs/eye-open.svg";
import { ReactComponent as EyeClose } from "assets/svgs/eye-close.svg";

const ChangePassword = () => {
  const {
    reset,
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const password = watch("password");

  const [loading, setLoading] = useState(false);
  const [hideOldPassword, setHideOldPassword] = useState(false);
  const [hideNewPassword, setHideNewPassword] = useState(false);
  const [hideConfirmNewPassword, setHideConfirmNewPassword] = useState(false);

  const updatePasswordMutation = useMutation(updateCognitoUserPassword, {
    onSuccess: () => {
      toast.success("Password updated", {
        position: "top-right",
      });
      reset();
    },
    onError: async (error) => {
      toast.error("Failed updating password", {
        position: "top-right",
      });
      console.error("Failed updating password:", error);
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const onSubmitUpdatePassword = (formData) => {
    try {
      setLoading(true);
      updatePasswordMutation.mutate({
        newPassword: formData.password,
        oldPassword: formData.oldPassword,
      });
    } catch (error) {}
  };

  return (
    <>
      <DashboardHeader
        label={"Change Password"}
        className={"xl:max-w-[850px] w-full mt-4 lg:w-full"}
      />

      <form
        onSubmit={handleSubmit(onSubmitUpdatePassword)}
        className={`mb-11 flex flex-col xl:max-w-[850px] ${
          loading ? "select-none" : ""
        }`}
      >
        {/* Current Password */}
        <div className="mt-8">
          <InputFieldLabel label={"Current Password"} className={"mb-1.5"} />
          <div className="relative">
            <input
              placeholder="Current Password"
              {...register("oldPassword", {
                required: "Current password cannot be empty",
                pattern: {
                  value:
                   /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]{8,}$/,
                  message:
                    "Password must be 8+ characters with an uppercase, lowercase, number, and special char.",
                },
              })}
              type={hideOldPassword ? "text" : "password"}
              className="input-field-classes py-2 px-[18px] pr-12 w-full text-base"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
              onClick={() => setHideOldPassword((s) => !s)}
            >
              {!hideOldPassword ? <EyeOpen /> : <EyeClose />}
            </button>
          </div>
          <div className="h-7">
            {errors && errors.oldPassword && (
              <span className="text-red text-sm">
                {errors.oldPassword.message}
              </span>
            )}
          </div>
        </div>

        {/* New Password */}
        <div>
          <InputFieldLabel label={"New Password"} className={"mb-1.5"} />
          <div className="relative">
            <input
              placeholder="New Password"
              {...register("password", {
                required: "Password cannot be empty",
                pattern: {
                  value:
                   /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]{8,}$/,
                  message:
                    "Password must be 8+ characters with an uppercase, lowercase, number, and special char.",
                },
              })}
              type={hideNewPassword ? "text" : "password"}
              className="input-field-classes py-2 px-[18px] pr-12 w-full text-base"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
              onClick={() => setHideNewPassword((s) => !s)}
            >
              {!hideNewPassword ? <EyeOpen /> : <EyeClose />}
            </button>
          </div>
          <div className="h-7">
            {errors && errors.password && (
              <span className="text-red text-sm">{errors.password.message}</span>
            )}
          </div>
        </div>

        {/* Confirm New Password */}
        <div>
          <InputFieldLabel
            label={"Confirm New Password"}
            className={"mb-1.5"}
          />
          <div className="relative">
            <input
              placeholder="Confirm New Password"
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === password || "The passwords do not match",
              })}
              type={hideConfirmNewPassword ? "text" : "password"}
              className="py-2 px-[18px] pr-12 input-field-classes w-full text-base"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
              onClick={() => setHideConfirmNewPassword((s) => !s)}
            >
              {!hideConfirmNewPassword ? <EyeOpen /> : <EyeClose />}
            </button>
          </div>
          <div className="h-7">
            {errors && errors.confirmPassword && (
              <span className="text-red text-sm">
                {errors.confirmPassword.message}
              </span>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-[58px]">
          <button
            type="submit"
            disabled={loading}
            className="bg-green border border-solid border-gray-600 rounded-lg p-2 text-base px-4 py-2 relative disabled:cursor-not-allowed hover:bg-green/90 transition-colors"
          >
            <span className={loading ? "opacity-0" : "opacity-100"}>
              Update Password
            </span>
            {loading && (
              <ScaleLoader
                loading={loading}
                className="absolute top-[60%] left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              />
            )}
          </button>
        </div>
      </form>
    </>
  );
};

export default ChangePassword;